import { FOOTSTEP_TIMING } from '../config/audio-tuning';
import { FootstepTracker } from './footstep-tracker';

/** What the frame loop knows about a body that might be making noise. */
export interface FootstepEntity {
  id: string;
  x: number;
  z: number;
  /** `EntityStateName` — only the movement states produce steps. */
  state: string;
  /** Mid-jump (2026-10-16): feet off the floor make no steps, whatever the state says. */
  airborne?: boolean;
}

export interface RemoteStep {
  x: number;
  z: number;
  isSprinting: boolean;
  /** Crouching bodies step quieter. See `CROUCH_GAIN_MULTIPLIER`. */
  gainMultiplier: number;
}

/**
 * A crouched body is not silent, but it is trying not to be heard.
 *
 * The server grants no stealth for this yet — `noise.heard` and the whole
 * propagation half of noise-interest-management.md are unimplemented, so
 * crouching currently changes only your speed (1.4 m/s) and this. Which makes
 * the quieter step the *only* thing that presently rewards crouching at all.
 */
export const CROUCH_GAIN_MULTIPLIER = 0.4;

const MOVING_STATES = new Set(['MOVING', 'SPRINTING', 'CROUCHING']);

/**
 * Per-body stride cadence for everything that is not the local player.
 *
 * One `FootstepTracker` per entity, because cadence is per-body: two delvers
 * walking are not in step, and driving both from one clock would make them
 * sound like one. Trackers are pruned for entities that vanish, which is the
 * common case rather than the exception — interest management adds and removes
 * bodies from `room.state` constantly as they cross the 10m sight radius, and a
 * map that only ever grew would leak an entry per entity seen per run.
 */
export class RemoteFootsteps {
  private readonly trackers = new Map<string, FootstepTracker>();

  /**
   * Advance every body's cadence, reporting the steps that landed this frame.
   *
   * Returns them rather than playing them so this stays a pure state machine,
   * testable without Web Audio — the same reasoning `FootstepTracker` itself
   * documents.
   */
  update(dtSeconds: number, entities: readonly FootstepEntity[]): RemoteStep[] {
    const steps: RemoteStep[] = [];
    const seen = new Set<string>();

    for (const entity of entities) {
      seen.add(entity.id);
      let tracker = this.trackers.get(entity.id);
      if (!tracker) {
        tracker = new FootstepTracker(FOOTSTEP_TIMING);
        this.trackers.set(entity.id, tracker);
      }

      const isSprinting = entity.state === 'SPRINTING';
      const isMoving = MOVING_STATES.has(entity.state) && !entity.airborne;
      if (tracker.advance(dtSeconds, isMoving, isSprinting) === null) continue;

      steps.push({
        x: entity.x,
        z: entity.z,
        isSprinting,
        gainMultiplier: entity.state === 'CROUCHING' ? CROUCH_GAIN_MULTIPLIER : 1,
      });
    }

    for (const id of this.trackers.keys()) {
      if (!seen.has(id)) this.trackers.delete(id);
    }
    return steps;
  }

  /** Bodies currently being tracked — exposed for the leak test, and for debugging. */
  get trackedCount(): number {
    return this.trackers.size;
  }

  reset(): void {
    this.trackers.clear();
  }
}
